import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import {
  Axios,
  Checkbox,
  DataTable,
  Grid,
  Table,
  ConditionWrapper,
  BaseForm as Form,
  Segment,
  Text,
} from 'component/base'
import { Custom } from 'component/pieces'

export const ShiireList = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
    onSelectChange,
    refresh,
  } = props

  const query = useMemo(
    () =>
      queryString.parse(window.location.search, {
        parseNumbers: true,
        parseBooleans: true,
      }),
    [window.location.search]
  )

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const defaultCondition = useMemo(() => {
    return {}
  }, [])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [displayData, setdisplayData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
        ? [selectedId]
        : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data, row => _.find(selectedIds, id => row.id === id)),
      data
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
    setSelectedIds([])
  }, [condition, refresh])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)
    const { data, error } = await Axios.ajax.get(
      '/api/staff/buppan/shiire/list',
      {
        params: { ...condition },
      }
    )
    setLoading(false)
    if (error) return
    setData(data)
    setSelectedIds([])
  }

  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
      />
    ),
    [history, handleChangeSelect]
  )

  useEffect(
    () => setdisplayData(_(data).orderBy(['shiire_id'], ['asc']).value()),
    [data]
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          defaultCondition={defaultCondition}
          condition={condition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElement}
          enableSidebar={false}
          compact
        >
          <DataTable
            name='shiireList'
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={TableHeader}
            tableRow={TableRowWithProps}
            data={displayData}
            bottomControl={<></>}
            selectedIds={selectedIds}
          />
        </ConditionWrapper>
      </Segment>
    </>
  )
})

ShiireList.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
}

ShiireList.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => {},
}

const TableHeader = props => {
  const { column, direction, withOrder, handleSort } = props

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'id' ? direction : null}
        onClick={() => handleSort('id')}
        content='仕入番号'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'shiire_date' ? direction : null}
        onClick={() => handleSort('shiire_date')}
        content='仕入日'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'busho_name' ? direction : null}
        onClick={() => handleSort('busho_name')}
        content='仕入部門'
      />
      <Table.HeaderCell
        style={{ width: '14.0rem' }}
        sorted={column === 'preferred_username' ? direction : null}
        onClick={() => handleSort('preferred_username')}
        content='担当者'
      />
      <Table.HeaderCell
        style={{ width: '35.0rem' }}
        sorted={column === 'torihikisaki_name' ? direction : null}
        onClick={() => handleSort('torihikisaki_name')}
        content='仕入先'
      />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'gokei_kingaku' ? direction : null}
        onClick={() => handleSort('gokei_kingaku')}
        content='合計金額'
      />
    </Table.Row>
  )
}

const TableRow = props => {
  const { withOrder, data, selectedIds, handleChangeSelect } = props || {}

  const {
    id,
    shiire_date,
    busho_name,
    preferred_username,
    torihikisaki_name,
    gokei_kingaku,
  } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={id} />
        }
      />
      <Table.Cell style={{ width: '10.0rem' }} content={id} />
      <Table.Cell style={{ width: '10.0rem' }} content={shiire_date} />
      <Table.Cell style={{ width: '10.0rem' }} content={busho_name} />
      <Table.Cell style={{ width: '14.0rem' }} content={preferred_username} />
      <Table.Cell style={{ width: '35.0rem' }} content={torihikisaki_name} />
      <Table.Cell
        style={{ width: '10.0rem', textAlign: 'right' }}
        content={<Text.Comma content={gokei_kingaku} />}
      />
    </Table.Row>
  )
}

const SearchElement = props => {
  const { nav, condition } = props

  const {
    shiire_id,
    shiire_date_from,
    shiire_date_to,
    torihikisaki_id,
    busho_id,
  } = condition

  return (
    <>
      <Grid.Column style={{ display: !nav || shiire_id ? 'block' : 'none' }}>
        <Form.Textbox.Standard
          value={shiire_id}
          label='仕入番号'
          name='shiire_id'
        />
      </Grid.Column>
      <Grid.Column
        style={{ display: !nav || shiire_date_from ? 'block' : 'none' }}
      >
        <Form.Textbox.DatePicker
          value={shiire_date_from}
          label='仕入日From'
          name='shiire_date_from'
        />
      </Grid.Column>
      <Grid.Column
        style={{ display: !nav || shiire_date_to ? 'block' : 'none' }}
      >
        <Form.Textbox.DatePicker
          value={shiire_date_to}
          label='仕入日To'
          name='shiire_date_to'
        />
      </Grid.Column>
      <Grid.Column
        style={{ display: !nav || torihikisaki_id ? 'block' : 'none' }}
      >
        <Custom.Dropdown.Shiiresaki
          value={torihikisaki_id}
          label='仕入先'
          name='torihikisaki_id'
          clearable
        />
      </Grid.Column>
      <Grid.Column style={{ display: !nav || busho_id ? 'block' : 'none' }}>
        <Custom.Dropdown.Busho
          value={busho_id}
          label='仕入部門'
          name='busho_id'
          enableAuthValue={false}
          clearable
        />
      </Grid.Column>
    </>
  )
}
